import React, { useState, useEffect, useRef } from 'react';
import { 
  FileText, 
  Download, 
  Calendar, 
  Building, 
  BarChart3, 
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Clock,
  DollarSign
} from 'lucide-react';

const AIReport = () => {
  const [reportTypes, setReportTypes] = useState([]);
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [selectedReportType, setSelectedReportType] = useState('wip');
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedDivision, setSelectedDivision] = useState('');
  const [inputType, setInputType] = useState('project'); // 'project' or 'division'
  const [projectSearch, setProjectSearch] = useState('');
  const [showProjectDropdown, setShowProjectDropdown] = useState(false);
  const [projectSearchLoading, setProjectSearchLoading] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth()); // Default to previous month
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const projectDropdownRef = useRef(null);

  // Load report types and projects on component mount
  useEffect(() => {
    loadReportTypes();
    loadProjects(); // This will load first 50 projects
    loadDivisions(); // Load divisions
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (projectDropdownRef.current && !projectDropdownRef.current.contains(event.target)) {
        setShowProjectDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      // Clean up search timeout
      if (window.searchTimeout) {
        clearTimeout(window.searchTimeout);
      }
    };
  }, []);

  const loadReportTypes = async () => {
    try {
      const response = await fetch('/api/ai-report/report-types');
      const data = await response.json();
      if (data.success) {
        setReportTypes(data.data);
      }
    } catch (error) {
      console.error('Error loading report types:', error);
    }
  };

  const loadProjects = async (prefix = '') => {
    try {
      setProjectSearchLoading(true);
      const url = prefix ? `/api/ai-report/projects?prefix=${encodeURIComponent(prefix)}` : '/api/ai-report/projects';
      
      const response = await fetch(url);
      const data = await response.json();
      
      if (data.success) {
        // Deduplicate projects by project_id
        const uniqueProjects = data.data.filter((project, index, self) => 
          index === self.findIndex(p => p.project_id === project.project_id)
        );
        
        if (prefix) {
          setFilteredProjects(uniqueProjects);
        } else {
          // Load ALL projects for local filtering, but show only first 50 in dropdown
          setProjects(uniqueProjects);
          setFilteredProjects(uniqueProjects.slice(0, 50)); // Show first 50 by default
        }
      } else {
        console.error('API error:', data.error);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setProjectSearchLoading(false);
    }
  };

  const loadDivisions = async () => {
    try {
      console.log('Loading divisions...');
      const response = await fetch('/api/ai-report/divisions');
      const data = await response.json();
      
      if (data.success) {
        console.log('Divisions loaded:', data.data);
        setDivisions(data.data);
      } else {
        console.error('API error:', data.error);
      }
    } catch (error) {
      console.error('Error loading divisions:', error);
    }
  };

  // Handle project search input with immediate filtering
  const handleProjectSearch = (value) => {
    setProjectSearch(value);
    setShowProjectDropdown(true); // Always show dropdown when typing
    
    // Clear any existing timeout
    if (window.searchTimeout) {
      clearTimeout(window.searchTimeout);
    }
    
    if (value.length >= 1) {
      // First, filter locally for immediate response - PREFIX MATCHING
      const localFiltered = projects.filter(project => 
        project.project_id.toLowerCase().startsWith(value.toLowerCase())
      );
      
      // Deduplicate the filtered results
      const uniqueFiltered = localFiltered.filter((project, index, self) => 
        index === self.findIndex(p => p.project_id === project.project_id)
      );
      
      setFilteredProjects(uniqueFiltered);
      
      // Then do server search with debouncing for more comprehensive results
      window.searchTimeout = setTimeout(() => {
        loadProjects(value);
      }, 200); // Reduced delay
    } else {
      // Show first 50 projects when input is empty
      setFilteredProjects(projects.slice(0, 50));
    }
  };

  // Handle project selection
  const handleProjectSelect = (project) => {
    setSelectedProject(project.project_id);
    setProjectSearch(`${project.project_id} - ${project.project_description || 'No description'}`);
    setShowProjectDropdown(false);
  };

  const handleDivisionSelect = (division) => {
    setSelectedDivision(division);
    setSelectedProject(''); // Clear project selection when switching to division
    setReportData(null); // Clear previous report data
    setError(null); // Clear any errors
  };

  const handleInputTypeChange = (type) => {
    setInputType(type);
    setSelectedProject('');
    setSelectedDivision('');
    setProjectSearch('');
    setShowProjectDropdown(false);
    setReportData(null); // Clear previous report data
    setError(null); // Clear any errors
  };

  const generateReport = async () => {
    if (!selectedProject && !selectedDivision) {
      setError(inputType === 'project' ? 'Please select a project' : 'Please select a division');
      return;
    }

    // Validate that only past months can be selected
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth() + 1;
    
    if (year > currentYear) {
      setError('Please select a current or past year for reporting');
      return;
    }
    
    if (year === currentYear && month >= currentMonth) {
      setError('Please select a past month for reporting');
      return;
    }

    setLoading(true);
    setError(null);
    setReportData(null); // Clear previous report data before generating new one

    try {
      const response = await fetch('/api/ai-report/generate/wip', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectNumber: inputType === 'project' ? selectedProject : null,
          division: inputType === 'division' ? selectedDivision : null,
          year: year,
          month: month
        }),
      });

      const data = await response.json();
      
      if (data.success) {
        setReportData(data);
      } else {
        setError(data.error || 'Failed to generate report');
      }
    } catch (error) {
      setError('Failed to generate report');
      console.error('Error generating report:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = async () => {
    if (!selectedProject && !selectedDivision) {
      setError(inputType === 'project' ? 'Please select a project' : 'Please select a division');
      return;
    }

    try {
      const response = await fetch('/api/ai-report/download/wip', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          projectNumber: inputType === 'project' ? selectedProject : null,
          division: inputType === 'division' ? selectedDivision : null,
          year: year,
          month: month
        }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Get filename from Content-Disposition header or use default
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = 'Work_In_Progress_Report.xlsx';
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        setError('Failed to download Excel file');
      }
    } catch (error) {
      setError('Failed to download Excel file');
      console.error('Error downloading Excel:', error);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return '$0';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercent = (value) => {
    if (value === null || value === undefined) return '0.00%';
    return `${parseFloat(value).toFixed(2)}%`;
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <FileText className="mr-3 h-8 w-8 text-sylvan-600" />
            AI Report
          </h1>
          <p className="mt-2 text-gray-600">
            Generate comprehensive construction reports with AI-powered analysis
          </p>
        </div>

        {/* Report Type Selection */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Report Type</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {reportTypes.map((report) => (
              <div
                key={report.id}
                className={`p-4 border rounded-lg cursor-pointer transition-all ${
                  selectedReportType === report.id
                    ? 'border-sylvan-500 bg-sylvan-50'
                    : report.active
                    ? 'border-gray-200 hover:border-gray-300'
                    : 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                }`}
                onClick={() => report.active && setSelectedReportType(report.id)}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium text-gray-900">{report.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{report.description}</p>
                  </div>
                  <div className="flex items-center">
                    {report.active ? (
                      <CheckCircle className="h-5 w-5 text-green-500" />
                    ) : report.comingSoon ? (
                      <Clock className="h-5 w-5 text-yellow-500" />
                    ) : (
                      <AlertCircle className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>
                {report.comingSoon && (
                  <span className="inline-block mt-2 px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">
                    Coming Soon
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Report Configuration */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Report Configuration</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Input Type Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Building className="inline h-4 w-4 mr-1" />
                Report Scope
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="inputType"
                    value="project"
                    checked={inputType === 'project'}
                    onChange={(e) => handleInputTypeChange(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Single Project</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="inputType"
                    value="division"
                    checked={inputType === 'division'}
                    onChange={(e) => handleInputTypeChange(e.target.value)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Division (All Projects)</span>
                </label>
              </div>
            </div>

            {/* Project Selection */}
            {inputType === 'project' && (
              <div className="relative" ref={projectDropdownRef}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Building className="inline h-4 w-4 mr-1" />
                  Project Number
                </label>
              <div className="relative">
                <input
                  type="text"
                  value={projectSearch}
                  onChange={(e) => handleProjectSearch(e.target.value)}
                  onFocus={() => {
                    setShowProjectDropdown(true);
                    if (projectSearch.length === 0) {
                      setFilteredProjects(projects.slice(0, 50));
                    }
                  }}
                  placeholder="Type project number (e.g., 13-017, 1xxxxx)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sylvan-500 focus:border-sylvan-500"
                />
                {showProjectDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
                    {projectSearchLoading ? (
                      <div className="px-3 py-2 text-gray-500 flex items-center">
                        <Clock className="animate-spin h-4 w-4 mr-2" />
                        Searching projects...
                      </div>
                    ) : filteredProjects.length > 0 ? (
                      <>
                        {filteredProjects.map((project, index) => (
                          <div
                            key={`${project.project_id}-${index}`}
                            onClick={() => handleProjectSelect(project)}
                            className="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
                          >
                            <div className="font-medium text-gray-900">{project.project_id}</div>
                            <div className="text-sm text-gray-600">{project.project_description || 'No description'}</div>
                          </div>
                        ))}
                        {projectSearch && (
                          <div className="px-3 py-2 text-xs text-gray-400 border-t border-gray-200">
                            Showing {filteredProjects.length} projects matching "{projectSearch}"
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="px-3 py-2 text-gray-500">
                        {projectSearch ? `No projects found matching "${projectSearch}"` : 'No projects found'}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Start typing to search projects (e.g., "1" for all projects starting with 1)
              </p>
            </div>
            )}

            {/* Division Selection */}
            {inputType === 'division' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Building className="inline h-4 w-4 mr-1" />
                  Division
                </label>
                <select
                  value={selectedDivision}
                  onChange={(e) => handleDivisionSelect(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sylvan-500 focus:border-sylvan-500"
                >
                  <option value="">Select Division</option>
                  {divisions.map((division) => (
                    <option key={division.division} value={division.division}>
                      Division {division.division} ({division.projectCount} projects)
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Select a division to include all projects in that division
                </p>
              </div>
            )}

            {/* Year Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline h-4 w-4 mr-1" />
                Year
              </label>
              <select
                value={year}
                onChange={(e) => setYear(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sylvan-500 focus:border-sylvan-500"
              >
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map((yearNum) => {
                  const currentYear = new Date().getFullYear();
                  const isPastOrCurrentYear = yearNum <= currentYear;
                  
                  return (
                    <option 
                      key={yearNum} 
                      value={yearNum}
                      disabled={!isPastOrCurrentYear}
                      className={!isPastOrCurrentYear ? 'text-gray-400' : ''}
                    >
                      {yearNum}
                      {!isPastOrCurrentYear ? ' (Future)' : ''}
                    </option>
                  );
                })}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Only current and past years are available
              </p>
            </div>

            {/* Month Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <Calendar className="inline h-4 w-4 mr-1" />
                Month
              </label>
              <select
                value={month}
                onChange={(e) => setMonth(parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sylvan-500 focus:border-sylvan-500"
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((monthNum) => {
                  const currentDate = new Date();
                  const currentYear = currentDate.getFullYear();
                  const currentMonth = currentDate.getMonth() + 1;
                  
                  // Only show months that are in the past
                  const isPastMonth = year < currentYear || (year === currentYear && monthNum < currentMonth);
                  
                  return (
                    <option 
                      key={monthNum} 
                      value={monthNum}
                      disabled={!isPastMonth}
                      className={!isPastMonth ? 'text-gray-400' : ''}
                    >
                      {new Date(0, monthNum - 1).toLocaleString('default', { month: 'long' })}
                      {!isPastMonth ? ' (Current/Future)' : ''}
                    </option>
                  );
                })}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Only past months are available for reporting
              </p>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex space-x-4">
            <button
              onClick={generateReport}
              disabled={loading || (inputType === 'project' ? !selectedProject : !selectedDivision)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-sylvan-600 hover:bg-sylvan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sylvan-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Clock className="animate-spin h-4 w-4 mr-2" />
                  Generating...
                </>
              ) : (
                <>
                  <BarChart3 className="h-4 w-4 mr-2" />
                  Generate Report
                </>
              )}
            </button>

            {reportData && (
              <button
                onClick={downloadExcel}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sylvan-500"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Excel
              </button>
            )}
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        {/* Report Results */}
        {reportData && (
          <div className="space-y-6">
            {/* Detailed Report Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Work In Progress Report</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Generated for {selectedProject} - {year}/{month}
                </p>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full divide-y divide-gray-200 table-fixed">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-40">
                        Project
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-22">
                        Est. Revenue
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-18">
                        Est. Cost
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-18">
                        Gross Profit %
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-18">
                        % Complete
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-18">
                        Actual Cost
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-22">
                        Earned Revenue
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-18">
                        Earned Gross Profit %
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-18">
                        Billings
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-22">
                        Cost in Excess of Billings
                      </th>
                      <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-22">
                        Billings in Excess of Cost
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reportData.data.map((row, index) => {
                      const grossProfit = row.Estimated_Revenue - row.Estimated_Cost;
                      const grossProfitPercent = row.Estimated_Revenue > 0 ? 
                        ((grossProfit / row.Estimated_Revenue) * 100) : 0;
                      
                      return (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-3 py-4 text-left text-sm text-gray-900 w-40">
                            <div>
                              <div className="font-medium text-xs">{row.Project}</div>
                              <div className="text-xs text-gray-500 leading-tight">{row.Description}</div>
                            </div>
                          </td>
                          <td className="px-3 py-4 text-right text-sm text-gray-900 w-22">
                            {formatCurrency(row.Estimated_Revenue)}
                          </td>
                          <td className="px-3 py-4 text-right text-sm text-gray-900 w-18">
                            {formatCurrency(row.Estimated_Cost)}
                          </td>
                          <td className="px-3 py-4 text-right text-sm text-gray-900 w-18">
                            {formatPercent(grossProfitPercent)}
                          </td>
                          <td className="px-3 py-4 text-right text-sm text-gray-900 w-18">
                            {formatPercent(row.Percent_Complete)}
                          </td>
                          <td className="px-3 py-4 text-right text-sm text-gray-900 w-18">
                            {formatCurrency(row.Actual_Cost)}
                          </td>
                          <td className="px-3 py-4 text-right text-sm text-gray-900 w-22">
                            {formatCurrency(row.Earned_Revenue)}
                          </td>
                          <td className="px-3 py-4 text-right text-sm text-gray-900 w-18">
                            {formatPercent(row.Earned_Gross_Profit_Percent)}
                          </td>
                          <td className="px-3 py-4 text-right text-sm text-gray-900 w-18">
                            {formatCurrency(row.Billings)}
                          </td>
                          <td className="px-3 py-4 text-right text-sm text-gray-900 w-22">
                            {formatCurrency(row.Cost_in_Excess_of_Billings)}
                          </td>
                          <td className="px-3 py-4 text-right text-sm text-gray-900 w-22">
                            {formatCurrency(row.Billings_in_Excess_of_Cost)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIReport;
